'use client';

import { JSFunction } from '@ahamove/js-function';
import { decodeToken } from '@ahm/common-helpers';
import { useEffect, useMemo, useState } from 'react';
import { useGetUserProfile } from '../api';
import { useGetSupplierProfile } from '../api/use-get-supplier-profile';
import type { AuthContextProps } from '../auth-provider';
import { AuthStatus } from '../lib/constants';
import { useSearchParams } from '../lib/navigation';
import type { SocketClientConfig } from '../lib/utils';
import type { Locales } from '../locales';
import { useAuthStore } from '../store/auth-store-provider';
import {
  WEBINAPP_CONFIGS,
  isWebinappId,
  type WebInAppConfig,
} from '../webinapp';
import { useAuthHandlers } from './use-auth-handlers';
import { useAuthStatus } from './use-auth-status';

type UseAuthProviderValueProps = {
  isMobile?: boolean;
  lang?: keyof Locales;
  socketConfig?: SocketClientConfig;
};

export function useAuthProviderValue({
  isMobile = false,
  lang = 'vi',
  socketConfig,
}: UseAuthProviderValueProps): AuthContextProps {
  const { session, updateUserSession, updateSupplierSession } = useAuthStore(
    (state) => state
  );

  const searchParams = useSearchParams();
  const tokenQueryParam = searchParams.get('token');
  const webInAppSearchParam = searchParams.get('webinapp');

  const accessToken = useMemo(() => session?.token ?? null, [session]);

  const decodeTokenData = useMemo(() => {
    return accessToken ? decodeToken(accessToken) : null;
  }, [accessToken]);

  const { status, isAuthenticated } = useAuthStatus();

  const {
    isPendingTransition,
    isAuthMutationPending,
    apiError,
    loginData,
    loginViaSocial,
    loginViaPhoneNumber,
    handleActivateUser,
    handleUpdatePasscode,
    onReSendOTP,
    handleRequestOTP,
    logout,
    onRegister,
    registerData,
    getSession,
    page,
    handleForceLoginOTP,
    isForceLoginOTP,
    handleSkipCreatePasscode,
    showModalSelectOtpChannel,
    setShowModalSelectOtpChannel,
  } = useAuthHandlers({ lang, socketConfig });

  const [webInAppConfig, setWebInAppConfig] = useState<
    WebInAppConfig | undefined
  >();

  useEffect(() => {
    if (webInAppSearchParam && isWebinappId(webInAppSearchParam)) {
      const newConfig = WEBINAPP_CONFIGS[webInAppSearchParam];
      setWebInAppConfig(newConfig);
      JSFunction.call({ name: 'hide_toolbar' });
    }
  }, [webInAppSearchParam]);

  // const { data: user, isLoading } = useGetUserProfile({
  //   enabled:
  //     !!session?.token && status?.toString() === AuthStatus.Authenticated,
  // });

  const isUserEnabled = useMemo(() => {
    return (
      status?.toString() === AuthStatus.Authenticated &&
      decodeTokenData?.typ === 'user' &&
      (!tokenQueryParam || tokenQueryParam === accessToken)
    );
  }, [status, decodeTokenData, tokenQueryParam, accessToken]);

  const isSupplierEnabled = useMemo(() => {
    return (
      status?.toString() === AuthStatus.Authenticated &&
      decodeTokenData?.typ === 'supplier' &&
      (!tokenQueryParam || tokenQueryParam === accessToken)
    );
  }, [status, decodeTokenData, tokenQueryParam, accessToken]);

  const { data: user, isFetching: isFetchingUser } = useGetUserProfile({
    enabled: isUserEnabled,
  });

  const { data: supplier, isFetching: isFetchingSupplier } =
    useGetSupplierProfile({ enabled: isSupplierEnabled });

  useEffect(() => {
    if (user) {
      updateUserSession(user);
    }
  }, [user]);

  useEffect(() => {
    if (supplier) {
      updateSupplierSession(supplier);
    }
  }, [supplier]);

  return useMemo(
    () => ({
      status,
      isAuthenticated,
      loginViaSocial,
      loginViaPhoneNumber,
      logout,
      handleUpdatePasscode,
      handleActivateUser,
      loading:
        isFetchingUser ||
        isFetchingSupplier ||
        isPendingTransition ||
        isAuthMutationPending,
      isWebInApp: Boolean(isMobile && webInAppConfig?.id),
      webInAppConfig,
      loginData,
      apiError,
      onReSendOTP,
      handleRequestOTP,
      onRegister,
      session,
      getSession,
      registerData,
      page,
      handleForceLoginOTP,
      isForceLoginOTP,
      handleSkipCreatePasscode,
      isAuthMutationPending,
      showModalSelectOtpChannel,
      setShowModalSelectOtpChannel,
    }),
    [
      status,
      isMobile,
      webInAppConfig,
      loginData,
      registerData,
      apiError,
      session,
      isFetchingUser,
      isFetchingSupplier,
      page,
      handleForceLoginOTP,
      isAuthenticated,
      loginViaSocial,
      loginViaPhoneNumber,
      logout,
      handleUpdatePasscode,
      handleActivateUser,
      onReSendOTP,
      handleRequestOTP,
      onRegister,
      getSession,
      isForceLoginOTP,
      handleSkipCreatePasscode,
      isPendingTransition,
      isAuthMutationPending,
      showModalSelectOtpChannel,
    ]
  );
}
